import { adkitApi } from "../adkitApi";
import { stripUndefinedParams } from "../queryParams";
import {
  ClubhouseAdKitImageArraySchema,
  ClubhouseAdKitImageSchema,
  ClubhouseAdKitImagesUploadResponseSchema,
  type ClubhouseAdKitImageDto,
  type ClubhouseAdKitImagesUploadResponse,
} from "entities/responses/adkitImage";
import type { SluggedId } from "Models";

const urls = {
  images(facilityId: string): string {
    return `/api/ad-kit/facility/${facilityId}/images`;
  },
  image(facilityId: string, imageId: SluggedId): string {
    return `/api/ad-kit/facility/${facilityId}/image/${imageId}`;
  },
  restore(facilityId: string, imageId: SluggedId): string {
    return `${urls.image(facilityId, imageId)}/restore`;
  },
};

export interface ImageUploadFile {
  file: Blob;
  imageName: string;
  overrideWarnings?: boolean;
}

const EXTENSION_BY_TYPE: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
};

/** Windows device names, which can't be used as a file name even with an extension. */
const RESERVED_WINDOWS_NAMES = /^(con|prn|aux|nul|com\d|lpt\d)$/i;

/**
 * The API validates the multipart file name as a Windows file name with an
 * image extension, and rejects anything else with a 400. Every upload goes
 * through here — a picked file can carry a ":" on macOS just as easily as a
 * generated name like "Logo (2:1)" can, and a bare Blob would go out as "blob".
 */
export function uploadFileName(file: Blob, imageName: string): string {
  const source = file instanceof File && file.name ? file.name : imageName;
  const typedExtension = EXTENSION_BY_TYPE[file.type];
  const namedExtension = /\.(png|jpe?g)$/i.exec(source)?.[1]?.toLowerCase();
  const extension =
    typedExtension ??
    (namedExtension === "jpeg" ? "jpg" : namedExtension) ??
    "png";
  let base = [...source]
    .filter((char) => char.charCodeAt(0) >= 32)
    .join("")
    .replace(/\.[^./\\]+$/, "")
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/[. ]+$/, "")
    .trim();
  if (!base) base = "image";
  if (RESERVED_WINDOWS_NAMES.test(base)) base = `${base}-image`;
  return `${base}.${extension}`;
}

/** Multipart field names match the legacy uploader, which the API accepts. */
export function buildImageUploadFormData(files: ImageUploadFile[]): FormData {
  const formData = new FormData();
  files.forEach((f, i) => {
    formData.append(
      `Files[${i}].file`,
      f.file,
      uploadFileName(f.file, f.imageName),
    );
    formData.append(`Files[${i}].imageName`, f.imageName);
    formData.append(
      `Files[${i}].overrideWarnings`,
      String(f.overrideWarnings ?? false),
    );
  });
  return formData;
}

type FacilityArgs = { facilityId: string };
type GetImagesArgs = FacilityArgs & { includeDeletedImages?: boolean };
type ImageArgs = FacilityArgs & { imageId: SluggedId };
type UploadImagesArgs = FacilityArgs & { files: ImageUploadFile[] };
type ReplaceImageArgs = ImageArgs & {
  file: Blob;
  imageName: string;
  description?: string;
};
type PatchImageArgs = ImageArgs & {
  body: { name?: string; description?: string | null };
};

const imageEndpoints = adkitApi.injectEndpoints({
  endpoints: (build) => ({
    getFacilityImages: build.query<ClubhouseAdKitImageDto[], GetImagesArgs>({
      query: ({ facilityId, includeDeletedImages }) => ({
        url: urls.images(facilityId),
        params: stripUndefinedParams({ includeDeletedImages }),
      }),
      transformResponse: (raw: unknown) =>
        ClubhouseAdKitImageArraySchema.parse(raw),
      providesTags: (result) =>
        result
          ? [
              ...result.map((img) => ({ type: "Image" as const, id: img.id })),
              "Image",
            ]
          : ["Image"],
    }),

    uploadAdKitImages: build.mutation<
      ClubhouseAdKitImagesUploadResponse,
      UploadImagesArgs
    >({
      query: ({ facilityId, files }) => ({
        url: urls.images(facilityId),
        method: "POST",
        body: buildImageUploadFormData(files),
      }),
      transformResponse: (raw: unknown) =>
        ClubhouseAdKitImagesUploadResponseSchema.parse(raw),
      invalidatesTags: ["Image"],
    }),

    replaceAdKitImage: build.mutation<ClubhouseAdKitImageDto, ReplaceImageArgs>(
      {
        query: ({ facilityId, imageId, file, imageName, description }) => {
          const body = new FormData();
          body.append("File", file, uploadFileName(file, imageName));
          body.append("ImageName", imageName);
          if (description) body.append("Description", description);
          return { url: urls.image(facilityId, imageId), method: "PUT", body };
        },
        transformResponse: (raw: unknown) =>
          ClubhouseAdKitImageSchema.parse(raw),
        invalidatesTags: (_r, _e, { imageId }) => [
          { type: "Image", id: imageId },
          "Image",
        ],
      },
    ),

    patchAdKitImage: build.mutation<ClubhouseAdKitImageDto, PatchImageArgs>({
      query: ({ facilityId, imageId, body }) => ({
        url: urls.image(facilityId, imageId),
        method: "PATCH",
        body: { ...body, restoreDeleted: false },
      }),
      transformResponse: (raw: unknown) => ClubhouseAdKitImageSchema.parse(raw),
      invalidatesTags: (_r, _e, { imageId }) => [
        { type: "Image", id: imageId },
        "Image",
      ],
    }),

    deleteAdKitImage: build.mutation<void, ImageArgs>({
      query: ({ facilityId, imageId }) => ({
        url: urls.image(facilityId, imageId),
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { imageId }) => [
        { type: "Image", id: imageId },
        "Image",
      ],
    }),

    restoreAdKitImage: build.mutation<ClubhouseAdKitImageDto, ImageArgs>({
      query: ({ facilityId, imageId }) => ({
        url: urls.restore(facilityId, imageId),
        method: "PUT",
      }),
      transformResponse: (raw: unknown) => ClubhouseAdKitImageSchema.parse(raw),
      invalidatesTags: (_r, _e, { imageId }) => [
        { type: "Image", id: imageId },
        "Image",
      ],
    }),
  }),
});

export const {
  useGetFacilityImagesQuery,
  useUploadAdKitImagesMutation,
  useReplaceAdKitImageMutation,
  usePatchAdKitImageMutation,
  useDeleteAdKitImageMutation,
  useRestoreAdKitImageMutation,
} = imageEndpoints;
